import { API } from "@swivl-mono/api-helper";
import type { UserStoriesListResponse } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import {
  type BotPromptChannel,
  type Domain,
  KNOWN_TOOL_NAMES,
  type UserStory,
  type UserStoryStatus,
  deriveToolUsage,
  parseStoryNumber,
  storageCategoryOrder
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonVariant, SearchInput } from "@swivl-mono/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useDomain } from "../../../Models/Domain/Domain.Context";
import { Member } from "../../../Models/Member/Member.Current";
import { AttemptsSetting, readAttemptsPerCase } from "../../AIStudio/Evals/AttemptsSetting";
import { ConcurrencySetting, readConcurrency } from "../../AIStudio/Evals/ConcurrencySetting";
import { UserStoryAddModal } from "./Components/UserStoryAddModal";
import { UserStoryDetailModal } from "./Components/UserStoryDetailModal";
import { UserStoryRow } from "./Components/UserStoryRow";

const STORAGE_KEY_LAST_SEEN = "swivl.userStories.lastSeenAt";

interface FilterState {
  statuses: Set<UserStoryStatus>;
  channels: Set<BotPromptChannel>;
  hasEval: "any" | "yes" | "no";
  category: string; // "" = any
  tools: Set<string>; // empty = no tool filter
  searchQuery: string;
}

const DEFAULT_FILTER: FilterState = {
  statuses: new Set<UserStoryStatus>(["backlog", "building", "live"]),
  channels: new Set<BotPromptChannel>(),
  hasEval: "any",
  category: "",
  tools: new Set<string>(),
  searchQuery: ""
};

/**
 * Admin list view for project Snowy user stories.
 *
 * Loads from `GET /user-stories`, filters client-side, expands detail inline
 * on row click. "New since last visit" pill is computed from each story's
 * `updatedAt` vs. localStorage `lastSeenAt` (per Member.id).
 *
 * See plan: ancient-percolating-lantern.md §G.
 */
export function AdminUserStoriesEditor(): JSX.Element {
  const { activeDomain, activeManifest, studioPath } = useDomain();
  const navigate = useNavigate();
  const { storyId: selectedStoryId } = useParams<{ storyId?: string }>();
  const [data, setData] = useState<UserStoriesListResponse | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
  const [addingNew, setAddingNew] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState<string>(() => readLastSeenAt());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await API.UserStories.list();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load user stories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const handleSaved = (saved: UserStory): void => {
    setData((prev) => {
      if (!prev) return prev;
      const idx = prev.stories.findIndex((s) => s.id === saved.id);
      const next = [...prev.stories];
      if (idx >= 0) next[idx] = saved;
      else next.push(saved);
      return { ...prev, stories: next };
    });
    // Bump lastSeenAt past the just-saved story so it doesn't show as "new"
    bumpLastSeenAt(saved.updatedAt, setLastSeenAt);
  };

  const handleDeleted = (id: string): void => {
    setData((prev) => prev && { ...prev, stories: prev.stories.filter((s) => s.id !== id) });
    if (selectedStoryId === id) navigate(studioPath("user-stories"));
  };

  const openStory = (id: string): void => navigate(studioPath("user-stories", id));
  const closeStoryOverlay = (): void => navigate(studioPath("user-stories"));

  const selectedStory = useMemo(
    () => (selectedStoryId ? (data?.stories.find((s) => s.id === selectedStoryId) ?? null) : null),
    [selectedStoryId, data]
  );

  const handleMarkAllSeen = (): void => {
    bumpLastSeenAt(new Date().toISOString(), setLastSeenAt);
  };

  // Scope to the active domain (set by the AI Studio's domain picker in the
  // header). Stories from other domains stay loaded — switching domains via
  // the header re-renders this view without re-fetching.
  const domainScoped = useMemo(() => (data?.stories ?? []).filter((s) => s.domain === activeDomain), [data, activeDomain]);
  const filtered = useMemo(() => filterStories(domainScoped, filter), [domainScoped, filter]);
  const counts = useMemo(() => statusCounts(domainScoped), [domainScoped]);
  const newSinceLastVisit = useMemo(() => domainScoped.filter((s) => s.updatedAt > lastSeenAt).length, [domainScoped, lastSeenAt]);
  const categoryCoverage = useMemo(() => computeCategoryCoverage(domainScoped), [domainScoped]);
  const availableCategories = useMemo(() => uniqueCategories(domainScoped, activeDomain), [domainScoped, activeDomain]);
  const selectedStories = useMemo(() => domainScoped.filter((s) => selectedIds.has(s.id)), [domainScoped, selectedIds]);

  const toggleSelect = (id: string): void => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const clearSelection = (): void => setSelectedIds(new Set());

  const selectStoryIds = (ids: string[]): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  };

  const deselectStoryIds = (ids: string[]): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  };

  const handleBulkUpdate = async (mutate: (s: UserStory) => UserStory, label: string): Promise<void> => {
    if (selectedStories.length === 0) return;
    if (!confirm(`Apply "${label}" to ${selectedStories.length} stories?`)) return;
    setBulkPending(true);
    setBulkError(null);
    try {
      // Single batched request — server writes each affected domain file
      // once, so nx daemon's file watcher only restarts the bot/server
      // once per bulk action instead of N times.
      const updates = selectedStories.map(mutate);
      const { stories } = await API.UserStories.bulkUpsert(updates);
      for (const s of stories) handleSaved(s);
      clearSelection();
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : "Bulk update failed");
    } finally {
      setBulkPending(false);
    }
  };

  const handleBulkStatus = async (status: UserStoryStatus): Promise<void> => {
    await handleBulkUpdate(
      (s) => ({
        ...s,
        // Apply the new status to every channel the story already declares.
        channelStatus: Object.fromEntries(Object.keys(s.channelStatus).map((c) => [c, status])) as UserStory["channelStatus"]
      }),
      `set status = ${status}`
    );
  };

  const handleBulkCategory = async (category: string): Promise<void> => {
    await handleBulkUpdate((s) => ({ ...s, category }), `set category = ${category}`);
  };

  /**
   * Run the selected stories. Expands each story into one (storyId × channel)
   * pair per channel in `channelStatus` that (a) has an effective eval (shared
   * or per-channel override), and (b) is included by the channel filter (when
   * the user has narrowed to specific channels). Skips deprecated channels.
   *
   * Drops to the AI-Studio evaluations page on the new run.
   */
  const handleRunSelected = async (): Promise<void> => {
    if (!Member.orgId) return;
    const channelFilter = filter.channels;
    const selected: { caseId: string; channel: string }[] = [];
    const skippedNoEval: string[] = [];

    for (const s of selectedStories) {
      for (const [channelStr, status] of Object.entries(s.channelStatus)) {
        const channel = channelStr as BotPromptChannel;
        if (status === "deprecated") continue;
        if (channelFilter.size > 0 && !channelFilter.has(channel)) continue;
        const hasEval = s.evalByChannel?.[channel] !== undefined || s.eval !== undefined;
        if (!hasEval) {
          skippedNoEval.push(`${s.id}/${channel}`);
          continue;
        }
        selected.push({ caseId: s.id, channel });
      }
    }

    if (selected.length === 0) {
      setBulkError(
        skippedNoEval.length > 0
          ? `Selected stories have no eval defined yet (${skippedNoEval.length} channel-pairs skipped).`
          : "Nothing to run — try widening the channel filter."
      );
      return;
    }

    setBulkPending(true);
    setBulkError(null);
    try {
      const { runId } = await API.BotEval.startRun({
        sourceOrgId: Member.orgId,
        suite: "user-stories",
        selectedCases: selected,
        attemptsPerCase: readAttemptsPerCase(),
        concurrency: readConcurrency()
      });
      clearSelection();
      navigate(studioPath("testing", "evaluations", runId));
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : "Failed to start run");
    } finally {
      setBulkPending(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">User Stories — {activeManifest.displayName}</h1>
          <p className="text-sm text-grey-500">
            {data ? `${domainScoped.length} stories` : "Loading…"} · {counts.live} live · {counts.building} building · {counts.backlog} backlog ·{" "}
            {counts.deprecated} deprecated
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Eval attempts-per-case + concurrency settings — saved to
              localStorage by the shared widgets. Apply to bulk runs from this
              page AND single-story re-runs from the detail modal. */}
          <AttemptsSetting variant="compact" hideHelp />
          <ConcurrencySetting variant="compact" hideHelp />
          {newSinceLastVisit > 0 && (
            <Button color={ButtonColor.LIGHT_GREY} variant={ButtonVariant.GHOST} onClick={handleMarkAllSeen}>
              Mark all {newSinceLastVisit} new as seen
            </Button>
          )}
          {!data?.readOnly && (
            <Button color={ButtonColor.BLUE} onClick={() => setAddingNew(true)}>
              + Add Story
            </Button>
          )}
        </div>
      </div>

      {data?.readOnly && (
        <div className="rounded border border-grey-300 bg-grey-50 px-3 py-2 text-xs text-grey-700">
          <strong>Read-only.</strong> Edits aren&apos;t saved on this server — story files live in the local dev repo. Use this view to browse,
          search, and launch evals.
        </div>
      )}

      {!data?.readOnly && data?.uncommittedPaths && data.uncommittedPaths.length > 0 && (
        <div className="rounded border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
          <strong>{data.uncommittedPaths.length} file(s) with uncommitted changes.</strong> Run <code>git status</code> in the repo to commit.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={filter.searchQuery}
          onChange={(e) => setFilter({ ...filter, searchQuery: e.target.value })}
          placeholder="Search id, title, description, category…"
          className="min-w-[280px] grow"
        />
        <SelectAllToggle
          filteredIds={filtered.map((s) => s.id)}
          selectedIds={selectedIds}
          onSelectAll={() => selectStoryIds(filtered.map((s) => s.id))}
          onDeselectAll={() => deselectStoryIds(filtered.map((s) => s.id))}
        />
        <StatusFilters filter={filter} setFilter={setFilter} />
        <ChannelFilters filter={filter} setFilter={setFilter} />
        <CategoryFilter filter={filter} setFilter={setFilter} categories={availableCategories} />
        <HasEvalFilter filter={filter} setFilter={setFilter} />
        <ToolFilter filter={filter} setFilter={setFilter} />
      </div>

      <ActiveFilterChips filter={filter} setFilter={setFilter} totalCount={domainScoped.length} filteredCount={filtered.length} />

      {selectedStories.length > 0 && (
        <BulkActionsBar
          count={selectedStories.length}
          pending={bulkPending}
          error={bulkError}
          categories={availableCategories}
          onClear={clearSelection}
          onSetStatus={handleBulkStatus}
          onSetCategory={handleBulkCategory}
          onRun={handleRunSelected}
          channelFilter={filter.channels}
        />
      )}

      {error && <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="flex-1 overflow-y-auto rounded border border-grey-200">
        {loading && <div className="p-4 text-sm text-grey-500">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="p-4 text-sm text-grey-500">No stories match the current filters.</div>}
        {!loading &&
          filtered.map((story, idx) => {
            const prev = idx > 0 ? filtered[idx - 1] : undefined;
            const showHeader = !prev || prev.category !== story.category || prev.domain !== story.domain;
            const categoryStories = filtered.filter((s) => s.category === story.category && s.domain === story.domain);
            const categoryCount = categoryStories.length;
            const coverage = categoryCoverage[story.category];
            const categoryIds = categoryStories.map((s) => s.id);
            const selectedInCategoryCount = categoryIds.filter((id) => selectedIds.has(id)).length;
            return (
              <div key={story.id}>
                {showHeader && (
                  <CategoryDivider
                    domain={story.domain}
                    category={story.category}
                    count={categoryCount}
                    coverage={coverage}
                    selectedInCategoryCount={selectedInCategoryCount}
                    onSelectCategory={() => selectStoryIds(categoryIds)}
                    onDeselectCategory={() => deselectStoryIds(categoryIds)}
                  />
                )}
                <UserStoryRow
                  story={story}
                  isExpanded={selectedStoryId === story.id}
                  isNew={story.updatedAt > lastSeenAt}
                  onToggle={() => openStory(story.id)}
                  selectable={{ selected: selectedIds.has(story.id), onToggleSelect: () => toggleSelect(story.id) }}
                />
                {/* Detail rendered as a fullscreen overlay below — see end of view. */}
              </div>
            );
          })}
      </div>

      {addingNew && (
        <UserStoryAddModal
          defaultDomain={activeDomain}
          onClose={() => setAddingNew(false)}
          onAdded={(created) => {
            handleSaved(created);
            setAddingNew(false);
            openStory(created.id);
          }}
        />
      )}

      {/* Story detail modal — rendered inline so Router + DomainProvider context
          propagates into the modal content. (`SSModal` lives at App level and
          doesn't satisfy useNavigate/useDomain inside the dispatched JSX.) */}
      {selectedStoryId && selectedStory && (
        <UserStoryDetailModal story={selectedStory} onClose={closeStoryOverlay} onSaved={handleSaved} onDeleted={handleDeleted} />
      )}
    </div>
  );
}

// ─── Category divider ───────────────────────────────────────────────────────

interface CategoryCoverage {
  total: number;
  live: number;
  building: number;
  backlog: number;
  /** % of channel-status entries (across all stories in the category) that are "live". */
  liveCoveragePct: number;
}

interface CategoryDividerProps {
  domain: Domain;
  category: string;
  count: number;
  coverage?: CategoryCoverage;
  selectedInCategoryCount: number;
  onSelectCategory: () => void;
  onDeselectCategory: () => void;
}

function CategoryDivider({
  domain,
  category,
  count,
  coverage,
  selectedInCategoryCount,
  onSelectCategory,
  onDeselectCategory
}: CategoryDividerProps): JSX.Element {
  const allSelected = selectedInCategoryCount === count && count > 0;
  const someSelected = selectedInCategoryCount > 0 && selectedInCategoryCount < count;
  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-grey-200 bg-grey-100 px-3 py-1.5">
      <span className="text-[11px] font-semibold tracking-wide text-grey-500 uppercase">{domain}</span>
      <span className="text-grey-300">/</span>
      <span className="text-sm font-semibold text-grey-700">{category}</span>
      <span className="text-xs text-grey-400">({count})</span>

      {/* Per-category selection controls */}
      <span className="ml-2 flex items-center gap-1 text-[11px]">
        <button
          type="button"
          onClick={onSelectCategory}
          disabled={allSelected}
          className={filterClasses(
            "rounded border px-1.5 py-0.5",
            allSelected ? "cursor-default border-grey-200 text-grey-300" : "border-orange-300 text-orange-600 hover:bg-orange-50"
          )}
          title={allSelected ? "All selected" : `Select all ${count} in this category`}
        >
          Select all
        </button>
        <button
          type="button"
          onClick={onDeselectCategory}
          disabled={selectedInCategoryCount === 0}
          className={filterClasses(
            "rounded border px-1.5 py-0.5",
            selectedInCategoryCount === 0 ? "cursor-default border-grey-200 text-grey-300" : "border-grey-300 text-grey-600 hover:bg-grey-200"
          )}
          title={selectedInCategoryCount === 0 ? "Nothing to deselect" : `Deselect ${selectedInCategoryCount}`}
        >
          {someSelected ? `Deselect (${selectedInCategoryCount})` : "Deselect all"}
        </button>
      </span>

      {coverage && coverage.total > 0 && (
        <span className="ml-auto flex items-center gap-2 text-xs text-grey-500">
          <span className={filterClasses("font-medium", coverage.liveCoveragePct >= 75 ? "text-teal-600" : "text-grey-500")}>
            {coverage.live} / {coverage.total} live · {coverage.liveCoveragePct}%
          </span>
          {coverage.building > 0 && <span>{coverage.building} building</span>}
          {coverage.backlog > 0 && <span>{coverage.backlog} backlog</span>}
        </span>
      )}
    </div>
  );
}

// ─── Filter helpers ─────────────────────────────────────────────────────────

function filterStories(stories: UserStory[], filter: FilterState): UserStory[] {
  let result = stories;
  if (filter.statuses.size > 0 && filter.statuses.size < 4) {
    // Match if ANY channel's status is in the filter set.
    result = result.filter((s) => Object.values(s.channelStatus).some((status) => status && filter.statuses.has(status)));
  }
  if (filter.channels.size > 0) {
    // Match if the story applies to ANY of the selected channels.
    result = result.filter((s) => Object.keys(s.channelStatus).some((c) => filter.channels.has(c as BotPromptChannel)));
  }
  if (filter.hasEval === "yes") result = result.filter((s) => s.eval !== undefined);
  if (filter.hasEval === "no") result = result.filter((s) => s.eval === undefined);
  if (filter.category) result = result.filter((s) => s.category === filter.category);
  if (filter.tools.size > 0) {
    // Match if the story exercises ANY of the selected tools (OR semantics).
    // Tool usage is derived on the fly from each story's criteria — see
    // `deriveToolUsage` in `@swivl-mono/types`.
    result = result.filter((s) => {
      const used = deriveToolUsage(s);
      for (const t of filter.tools) if (used.has(t)) return true;
      return false;
    });
  }
  if (filter.searchQuery.trim().length > 0) {
    const q = filter.searchQuery.toLowerCase();
    result = result.filter(
      (s) =>
        s.id.toLowerCase().includes(q) ||
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q)
    );
  }
  return [...result].sort((a, b) => {
    if (a.domain !== b.domain) return a.domain < b.domain ? -1 : 1;
    if (a.category !== b.category) {
      // Domain-aware ordering: Storage uses STORAGE_CATEGORIES; other domains
      // fall back to alphabetical until they get their own canonical list.
      const aIdx = a.domain === "storage" ? storageCategoryOrder(a.category) : Number.MAX_SAFE_INTEGER;
      const bIdx = b.domain === "storage" ? storageCategoryOrder(b.category) : Number.MAX_SAFE_INTEGER;
      if (aIdx !== bIdx) return aIdx - bIdx;
      return a.category < b.category ? -1 : 1;
    }
    return parseStoryNumber(a.id) - parseStoryNumber(b.id);
  });
}

function uniqueCategories(stories: UserStory[], domain: Domain): string[] {
  const seen = new Set<string>();
  for (const s of stories) seen.add(s.category);
  // Prefer the canonical Storage ordering when scoped to that domain; fall
  // back to alphabetical for other domains until they get their own list.
  const list = [...seen];
  if (domain === "storage") {
    return list.sort((a, b) => storageCategoryOrder(a) - storageCategoryOrder(b) || (a < b ? -1 : 1));
  }
  return list.sort();
}

function computeCategoryCoverage(stories: UserStory[]): Record<string, CategoryCoverage> {
  const out: Record<string, CategoryCoverage> = {};
  for (const s of stories) {
    const c = s.category;
    if (!out[c]) out[c] = { total: 0, live: 0, building: 0, backlog: 0, liveCoveragePct: 0 };
    const statuses = Object.values(s.channelStatus) as UserStoryStatus[];
    out[c].total += statuses.length;
    for (const status of statuses) {
      if (status === "live") out[c].live++;
      else if (status === "building") out[c].building++;
      else if (status === "backlog") out[c].backlog++;
    }
  }
  for (const c of Object.keys(out)) {
    const v = out[c];
    v.liveCoveragePct = v.total === 0 ? 0 : Math.round((v.live / v.total) * 100);
  }
  return out;
}

function statusCounts(stories: UserStory[]): Record<UserStoryStatus, number> {
  // Count stories by their "worst" channel status, so the header reads
  // "X live · Y building · Z backlog" the same way a single-status
  // pill would. backlog > building > live > deprecated.
  const out: Record<UserStoryStatus, number> = { backlog: 0, building: 0, live: 0, deprecated: 0 };
  for (const s of stories) {
    const statuses = Object.values(s.channelStatus) as UserStoryStatus[];
    if (statuses.includes("backlog")) out.backlog++;
    else if (statuses.includes("building")) out.building++;
    else if (statuses.length > 0 && statuses.every((x) => x === "live")) out.live++;
    else out.deprecated++;
  }
  return out;
}

// ─── Filter UI components ───────────────────────────────────────────────────

interface FilterProps {
  filter: FilterState;
  setFilter: (f: FilterState) => void;
}

const STATUS_OPTIONS: UserStoryStatus[] = ["backlog", "building", "live", "deprecated"];

interface SelectAllToggleProps {
  filteredIds: string[];
  selectedIds: Set<string>;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

/**
 * Tri-state checkbox that selects every story currently visible under the
 * active filters. Indeterminate when only some filtered stories are selected.
 * "Select all" / "Deselect all" toggles based on whether any filtered ids
 * are already in the selection — operates only on the visible set, so the
 * selection of stories outside the current filter is preserved.
 */
function SelectAllToggle({ filteredIds, selectedIds, onSelectAll, onDeselectAll }: SelectAllToggleProps): JSX.Element {
  const ref = useRef<HTMLInputElement | null>(null);
  const total = filteredIds.length;
  const selectedCount = filteredIds.filter((id) => selectedIds.has(id)).length;
  const allSelected = total > 0 && selectedCount === total;
  const someSelected = selectedCount > 0 && selectedCount < total;

  // `indeterminate` isn't a JSX attribute — set imperatively so the checkbox
  // shows the dash-state when only part of the filtered set is selected.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = someSelected;
  }, [someSelected]);

  const label =
    total === 0
      ? "Select all (no matches)"
      : allSelected
        ? `Deselect all (${total})`
        : someSelected
          ? `Select all (${selectedCount}/${total})`
          : `Select all (${total})`;

  return (
    <label
      className={filterClasses(
        "flex items-center gap-1.5 rounded border border-grey-200 px-2 py-1 text-xs",
        total === 0 ? "cursor-not-allowed text-grey-400" : "cursor-pointer text-grey-700 hover:bg-grey-50"
      )}
      title="Select every story matching the current filters"
    >
      <input
        ref={ref}
        type="checkbox"
        className="cursor-pointer"
        checked={allSelected}
        disabled={total === 0}
        onChange={() => {
          if (allSelected || someSelected) onDeselectAll();
          else onSelectAll();
        }}
      />
      {label}
    </label>
  );
}

function StatusFilters({ filter, setFilter }: FilterProps): JSX.Element {
  return (
    <div className="flex items-center gap-1 rounded border border-grey-200 px-2 py-1">
      <span className="text-xs text-grey-500">Status:</span>
      {STATUS_OPTIONS.map((status) => {
        const active = filter.statuses.has(status);
        return (
          <button
            key={status}
            type="button"
            className={filterClasses(
              "rounded px-2 py-0.5 text-xs",
              active ? "bg-orange-500 text-white" : "bg-grey-100 text-grey-700 hover:bg-grey-200"
            )}
            onClick={() => {
              const next = new Set(filter.statuses);
              if (active) next.delete(status);
              else next.add(status);
              setFilter({ ...filter, statuses: next });
            }}
          >
            {statusEmoji(status)} {status}
          </button>
        );
      })}
    </div>
  );
}

const CHANNEL_OPTIONS: BotPromptChannel[] = ["web", "sms", "voice"];

function ChannelFilters({ filter, setFilter }: FilterProps): JSX.Element {
  return (
    <div className="flex items-center gap-1 rounded border border-grey-200 px-2 py-1">
      <span className="text-xs text-grey-500">Channels:</span>
      {CHANNEL_OPTIONS.map((channel) => {
        const active = filter.channels.has(channel);
        return (
          <button
            key={channel}
            type="button"
            className={filterClasses(
              "rounded px-2 py-0.5 text-xs",
              active ? "bg-orange-500 text-white" : "bg-grey-100 text-grey-700 hover:bg-grey-200"
            )}
            onClick={() => {
              const next = new Set(filter.channels);
              if (active) next.delete(channel);
              else next.add(channel);
              setFilter({ ...filter, channels: next });
            }}
          >
            {channel}
          </button>
        );
      })}
    </div>
  );
}

interface CategoryFilterProps extends FilterProps {
  categories: string[];
}

function CategoryFilter({ filter, setFilter, categories }: CategoryFilterProps): JSX.Element {
  return (
    <div className="flex items-center gap-1 rounded border border-grey-200 px-2 py-1">
      <span className="text-xs text-grey-500">Category:</span>
      <select
        value={filter.category}
        onChange={(e) => setFilter({ ...filter, category: e.target.value })}
        className="rounded bg-grey-100 px-1.5 py-0.5 text-xs text-grey-700"
      >
        <option value="">All</option>
        {categories.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </div>
  );
}

function HasEvalFilter({ filter, setFilter }: FilterProps): JSX.Element {
  return (
    <div className="flex items-center gap-1 rounded border border-grey-200 px-2 py-1">
      <span className="text-xs text-grey-500">Eval:</span>
      {(["any", "yes", "no"] as const).map((opt) => {
        const active = filter.hasEval === opt;
        return (
          <button
            key={opt}
            type="button"
            className={filterClasses(
              "rounded px-2 py-0.5 text-xs",
              active ? "bg-orange-500 text-white" : "bg-grey-100 text-grey-700 hover:bg-grey-200"
            )}
            onClick={() => setFilter({ ...filter, hasEval: opt })}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Multi-select dropdown for filtering stories by which bot tools their
 * criteria reference. Lists all `KNOWN_TOOL_NAMES` with a checkbox each;
 * the filter is OR semantics across selected tools (matching any one
 * keeps the story). Selected tools render as chips next to the trigger
 * so the active filter is visible at a glance even when the menu is closed.
 */
function ToolFilter({ filter, setFilter }: FilterProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = (tool: string): void => {
    const next = new Set(filter.tools);
    if (next.has(tool)) next.delete(tool);
    else next.add(tool);
    setFilter({ ...filter, tools: next });
  };

  const clear = (): void => setFilter({ ...filter, tools: new Set<string>() });

  const selected = [...filter.tools].sort();

  return (
    <div ref={ref} className="relative flex items-center gap-1 rounded border border-grey-200 px-2 py-1">
      <span className="text-xs text-grey-500">Tools:</span>
      <button type="button" className="rounded bg-grey-100 px-2 py-0.5 text-xs text-grey-700 hover:bg-grey-200" onClick={() => setOpen((v) => !v)}>
        {selected.length === 0 ? "Any" : `${selected.length} selected`}
      </button>
      {selected.length > 0 && (
        <button
          type="button"
          className="rounded px-1 py-0.5 text-xs text-grey-500 hover:bg-grey-100 hover:text-grey-700"
          onClick={clear}
          title="Clear tool filter"
        >
          ×
        </button>
      )}
      {open && (
        <div className="absolute top-full left-0 z-100 mt-1 max-h-80 w-56 overflow-y-auto rounded border border-grey-200 bg-white p-2 shadow-lg">
          {KNOWN_TOOL_NAMES.map((tool) => {
            const active = filter.tools.has(tool);
            return (
              <label key={tool} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-grey-50">
                <input type="checkbox" checked={active} onChange={() => toggle(tool)} />
                <span className="font-mono text-grey-700">{tool}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function statusEmoji(s: UserStoryStatus): string {
  if (s === "live") return "✅";
  if (s === "building") return "🟡";
  if (s === "backlog") return "🔴";
  return "⚫";
}

// ─── Bulk actions bar ───────────────────────────────────────────────────────

interface BulkActionsBarProps {
  count: number;
  pending: boolean;
  error: string | null;
  categories: string[];
  channelFilter: Set<BotPromptChannel>;
  onClear: () => void;
  onSetStatus: (status: UserStoryStatus) => Promise<void>;
  onSetCategory: (category: string) => Promise<void>;
  onRun: () => Promise<void>;
}

function BulkActionsBar({
  count,
  pending,
  error,
  categories,
  channelFilter,
  onClear,
  onSetStatus,
  onSetCategory,
  onRun
}: BulkActionsBarProps): JSX.Element {
  const channelScope = channelFilter.size === 0 ? "all channels" : [...channelFilter].join("/");
  return (
    <div className="border-primary-300 bg-primary-50 text-primary-800 flex flex-wrap items-center gap-2 rounded border px-3 py-2 text-xs">
      <span className="font-medium">{count} selected</span>
      <span className="text-grey-400">·</span>
      <button
        type="button"
        disabled={pending}
        onClick={() => void onRun()}
        className="border-primary-500 text-primary-700 hover:bg-primary-50 rounded border bg-white px-2 py-0.5 font-medium disabled:opacity-50"
        title={`Runs each (story × channel) pair on ${channelScope}. Stories without an eval are skipped.`}
      >
        ▶ Run on {channelScope}
      </button>
      <span className="text-grey-400">·</span>
      <span className="text-grey-600">Set status:</span>
      {(["backlog", "building", "live", "deprecated"] as UserStoryStatus[]).map((s) => (
        <button
          key={s}
          type="button"
          disabled={pending}
          onClick={() => void onSetStatus(s)}
          className="rounded border border-grey-300 bg-white px-2 py-0.5 hover:bg-grey-50 disabled:opacity-50"
        >
          {statusEmoji(s)} {s}
        </button>
      ))}
      <span className="text-grey-400">·</span>
      <span className="text-grey-600">Set category:</span>
      <select
        disabled={pending}
        onChange={(e) => {
          if (e.target.value) {
            void onSetCategory(e.target.value);
            e.target.value = "";
          }
        }}
        defaultValue=""
        className="rounded border border-grey-300 bg-white px-1.5 py-0.5 disabled:opacity-50"
      >
        <option value="" disabled>
          Choose…
        </option>
        {categories.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <span className="ml-auto flex items-center gap-2">
        {pending && <span className="text-grey-500 italic">Saving…</span>}
        {error && <span className="text-red-600">{error}</span>}
        <button type="button" onClick={onClear} className="rounded border border-grey-300 bg-white px-2 py-0.5 hover:bg-grey-50">
          Clear
        </button>
      </span>
    </div>
  );
}

// ─── localStorage helpers ───────────────────────────────────────────────────

function readLastSeenAt(): string {
  try {
    const memberId = Member.id;
    if (!memberId) return "1970-01-01T00:00:00Z";
    const raw = localStorage.getItem(STORAGE_KEY_LAST_SEEN);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    return map[String(memberId)] ?? "1970-01-01T00:00:00Z";
  } catch {
    return "1970-01-01T00:00:00Z";
  }
}

function bumpLastSeenAt(when: string, setLastSeenAt: (s: string) => void): void {
  try {
    const memberId = Member.id;
    if (!memberId) return;
    const raw = localStorage.getItem(STORAGE_KEY_LAST_SEEN);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    if (!map[String(memberId)] || map[String(memberId)] < when) {
      map[String(memberId)] = when;
      localStorage.setItem(STORAGE_KEY_LAST_SEEN, JSON.stringify(map));
      setLastSeenAt(when);
    }
  } catch {
    // ignore
  }
}

interface ActiveFilterChipsProps {
  filter: FilterState;
  setFilter: (f: FilterState) => void;
  totalCount: number;
  filteredCount: number;
}

function ActiveFilterChips({ filter, setFilter, totalCount, filteredCount }: ActiveFilterChipsProps): JSX.Element | null {
  const chips: { label: string; clear: () => void }[] = [];

  if (filter.searchQuery.trim()) {
    chips.push({ label: `“${filter.searchQuery.trim()}”`, clear: () => setFilter({ ...filter, searchQuery: "" }) });
  }
  for (const status of filter.statuses) {
    if (DEFAULT_FILTER.statuses.has(status)) continue; // baseline; skip noise
    chips.push({
      label: `status: ${status}`,
      clear: () => {
        const next = new Set(filter.statuses);
        next.delete(status);
        setFilter({ ...filter, statuses: next });
      }
    });
  }
  // If statuses differs from default, indicate via a single chip ("custom statuses").
  const statusDiff = filter.statuses.size !== DEFAULT_FILTER.statuses.size || [...filter.statuses].some((s) => !DEFAULT_FILTER.statuses.has(s));
  if (statusDiff && filter.statuses.size === 0) {
    chips.push({ label: "no status filter", clear: () => setFilter({ ...filter, statuses: new Set(DEFAULT_FILTER.statuses) }) });
  }
  for (const ch of filter.channels) {
    chips.push({
      label: `channel: ${ch}`,
      clear: () => {
        const next = new Set(filter.channels);
        next.delete(ch);
        setFilter({ ...filter, channels: next });
      }
    });
  }
  if (filter.category) {
    chips.push({ label: `category: ${filter.category}`, clear: () => setFilter({ ...filter, category: "" }) });
  }
  if (filter.hasEval !== "any") {
    chips.push({ label: `eval: ${filter.hasEval}`, clear: () => setFilter({ ...filter, hasEval: "any" }) });
  }
  for (const tool of filter.tools) {
    chips.push({
      label: `tool: ${tool}`,
      clear: () => {
        const next = new Set(filter.tools);
        next.delete(tool);
        setFilter({ ...filter, tools: next });
      }
    });
  }

  const clearAll = (): void => setFilter(DEFAULT_FILTER);
  const showCount = filteredCount !== totalCount;

  if (chips.length === 0 && !showCount) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {showCount && (
        <span className="text-grey-500">
          {filteredCount} of {totalCount}
        </span>
      )}
      {chips.map((chip, i) => (
        <span key={i} className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-orange-700">
          {chip.label}
          <button type="button" onClick={chip.clear} className="text-orange-600 hover:text-orange-800" aria-label={`Clear ${chip.label}`}>
            ×
          </button>
        </span>
      ))}
      {chips.length > 0 && (
        <button type="button" onClick={clearAll} className="text-grey-500 hover:text-orange-600">
          Clear all
        </button>
      )}
    </div>
  );
}
